// src/components/ErrorBoundary.js
import React from "react";
import { captureException } from "../lib/errorTracking";
import { logError } from "../lib/logger";
import { getUserFriendlyMessage, getErrorGuidance } from "../lib/errorUtils";
import "./ErrorBoundary.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      userMessage: null,
      guidance: null,
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details using logger
    logError("ErrorBoundary caught an error", error, {
      componentStack: errorInfo.componentStack,
      errorInfo,
    });

    // Get user-friendly message and guidance
    const userMessage = getUserFriendlyMessage(error, "Something unexpected happened");
    const guidance = getErrorGuidance(error);

    // Store error details in state for display
    this.setState({
      error,
      errorInfo,
      userMessage,
      guidance,
    });

    // Send error to error tracking service
    captureException(error, {
      react: {
        componentStack: errorInfo.componentStack,
      },
      tags: {
        errorBoundary: true,
        component: errorInfo.componentStack?.split("\n")[1] || "unknown",
      },
      extra: {
        errorInfo,
        userMessage,
        guidance,
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Optionally reload the page
    // window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h1 className="error-boundary-title">Something went wrong</h1>
            <p className="error-boundary-message">
              {this.state.userMessage ||
                "We're sorry, but something unexpected happened. Please try refreshing the page."}
            </p>

            {this.state.guidance && (
              <div className="error-boundary-guidance">
                <p className="error-boundary-guidance-title">
                  <strong>What you can do:</strong>
                </p>
                <ul className="error-boundary-guidance-steps">
                  {this.state.guidance.steps?.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>
            )}

            {import.meta.env.DEV && this.state.error && (
              <details className="error-boundary-details">
                <summary className="error-boundary-summary">
                  Error Details (Development Only)
                </summary>
                <div className="error-boundary-stack">
                  <pre className="error-boundary-pre">
                    {this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}

            <div className="error-boundary-actions">
              <button onClick={this.handleReset} className="error-boundary-button">
                Try Again
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="error-boundary-button error-boundary-button--secondary"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
